/**
 * @module ReceiptChartScreen
 * @description This module renders a chart displaying the total daily receipt count.
 */

import React, { useEffect, useContext } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Container, Row, Col, Button } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js/auto'
import Loader from '../components/Loader.js'
import Message from '../components/Message.js'
import { getDailyTotalsByOwner } from '../actions/metaActions.js'
import { ThemeContext } from '../components/ThemeContext.js'

/**
 * @function ReceiptDailyCount
 * @description Component that renders a bar chart of daily total receipts.
 * It fetches the daily totals from the backend and displays them using a bar chart.
 * @returns {JSX.Element} The rendered component.
 */
const ReceiptDailyCount = () => {
  const { theme, isDarkMode } = useContext(ThemeContext)
  const dispatch = useDispatch()

  const ownerLogin = useSelector((state) => state.ownerLogin)
  const { ownerInfo } = ownerLogin

  const metaDailyTotalByOwner = useSelector(
    (state) => state.metaDailyTotalByOwner
  )
  const { dailyTotals, loading, error } = metaDailyTotalByOwner

  useEffect(() => {
    if (dailyTotals === undefined && ownerInfo) {
      dispatch(getDailyTotalsByOwner(ownerInfo.cid))
    }
  }, [dispatch, dailyTotals, ownerInfo])

  let datearray = []
  let countarray = []
  if (dailyTotals) {
    for (let i = 0; i < dailyTotals.length; i++) {
      datearray.push(dailyTotals[i].date)
      countarray.push(dailyTotals[i].count)
    }
  }

  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
  )

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: { color: theme.text }
      },
      title: {
        display: true,
        text: 'Daily Receipt Total Count',
        color: theme.text
      },
      font: {
        size: 18
      }
    },
    scales: {
      x: {
        ticks: { color: theme.subtext },
        grid: { color: isDarkMode ? '#2D342D' : '#DFE6E9' }
      },
      y: {
        ticks: { color: theme.subtext },
        grid: { color: isDarkMode ? '#2D342D' : '#DFE6E9' }
      }
    }
  }

  return (
    <>
      <Container fluid>
        <Row>
          <Col>
            <Button
              as={Link}
              to='/ownerdashboard'
              className='btn btn-sm my-2'
              variant='outline-primary'
              style={{
                borderColor: theme.accent,
                color: theme.accent,
                backgroundColor: 'transparent'
              }}
            >
              Back to Dashboard
            </Button>
            {loading && <Loader />}
            {error && <Message variant='danger'>{error}</Message>}
            {dailyTotals && (
              <Bar
                data={{
                  labels: datearray,
                  datasets: [
                    {
                      label: 'Count/Day',
                      backgroundColor: 'rgba(75,192,192,0.2)',
                      borderColor: 'rgba(75,192,192,1)',
                      borderWidth: 2,
                      hoverBackgroundColor: 'rgba(75,192,192,0.4)',
                      hoverBorderColor: 'rgba(75,192,192,1)',
                      data: countarray
                    }
                  ]
                }}
                options={options}
              />
            )}
          </Col>
        </Row>
      </Container>
    </>
  )
}

export default ReceiptDailyCount
